// "Schedule message" modal. Presets + a cross-platform date/time picker +
// a live send-time preview, matching the Ping schedule design. Fully
// NativeWind-styled; the date/time control is `DateTimePicker`, built from
// RN primitives so it works on react-native-web AND react-native-macos
// without a native-module dependency (no raw `<input>`).

import React, { useRef, useState } from "react";
import { Pressable, Text, View } from "react-native";
import { CalendarClock, X } from "lucide-react-native";
import { Modal } from "../../components/Modal";
import { AnchoredPopover } from "../../components/AnchoredPopover";
import { useColorScheme } from "../../foundations/colorScheme";
import { tokens } from "../../design-system/tokens";
import { DateTimePicker } from "../../composer/DateTimePicker";
import { SCHEDULE_PRESETS, formatScheduleSummary } from "./schedulePresets";

/** "25/07/2026, 10:48 AM" — the compact value shown in the Send-at field. */
function formatFieldValue(d: Date): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

export interface ScheduleMessageModalProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (sendAt: Date) => void;
  readonly initialDate?: Date;
}

function initialSendAt(initialDate?: Date): Date {
  const d = initialDate ?? new Date();
  d.setSeconds(0, 0);
  return d;
}

export function ScheduleMessageModal({
  visible,
  onClose,
  onConfirm,
  initialDate,
}: ScheduleMessageModalProps): React.ReactElement {
  const scheme = useColorScheme();
  const colorScheme: "light" | "dark" = scheme === "dark" ? "dark" : "light";
  const colors = tokens.color.semantic[colorScheme];

  const [sendAt, setSendAt] = useState<Date>(() => initialSendAt(initialDate));
  const [pickerOpen, setPickerOpen] = useState(false);
  const fieldRef = useRef<View>(null);
  const summary = formatScheduleSummary(sendAt);

  const isPresetActive = (compute: () => Date): boolean => {
    const c = compute();
    c.setSeconds(0, 0);
    return c.getTime() === sendAt.getTime();
  };

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      accessibilityLabel="Schedule message"
    >
      <View className="w-[440px] max-w-full gap-4">
        {/* Header */}
        <View className="flex-row items-start justify-between">
          <Text className="font-sans text-[22px] font-bold text-light-text-primary dark:text-white">
            Schedule message
          </Text>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close"
            className="p-[2px] active:opacity-60"
            testID="schedule-modal-close"
          >
            <X size={22} strokeWidth={2} color={colors.text.secondary} />
          </Pressable>
        </View>

        <Text className="font-sans text-[14px] leading-5 text-light-text-secondary dark:text-[#8B8F97]">
          We'll send your message within a minute of the scheduled time.
        </Text>

        {/* Preset chips */}
        <View className="flex-row flex-wrap gap-2">
          {SCHEDULE_PRESETS.map((preset) => {
            const active = isPresetActive(preset.compute);
            return (
              <Pressable
                key={preset.id}
                onPress={() => {
                  const d = preset.compute();
                  d.setSeconds(0, 0);
                  setSendAt(d);
                }}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                className={`
                  rounded-pill border px-4 py-[10px]
                  ${
                    active
                      ? "border-light-accent-indigo dark:border-[#537EFF] bg-light-accent-indigo/10 dark:bg-[#537EFF]/15"
                      : "border-light-border-secondary dark:border-[#2A2D34]"
                  }
                `}
                testID={`schedule-preset-${preset.id}`}
              >
                <Text
                  className={`font-sans text-[15px] font-semibold ${
                    active
                      ? "text-light-accent-indigo dark:text-[#8FA9FF]"
                      : "text-light-text-primary dark:text-white"
                  }`}
                >
                  {preset.label}
                </Text>
              </Pressable>
            );
          })}
        </View>

        {/* Send at — a field that opens the calendar + time picker in an
            anchored popover (shared AnchoredPopover), like a native date
            input's dropdown. */}
        <View className="gap-2">
          <Text className="font-sans text-[14px] text-light-text-secondary dark:text-[#8B8F97]">
            Send at
          </Text>
          <View ref={fieldRef}>
            <Pressable
              onPress={() => setPickerOpen((o) => !o)}
              accessibilityRole="button"
              accessibilityState={{ expanded: pickerOpen }}
              className="flex-row items-center justify-between rounded-[10px] border border-light-border-secondary dark:border-[#2A2D34] px-[14px] py-[13px] active:opacity-80"
              testID="schedule-datetime-field"
            >
              <Text className="font-sans text-[16px] text-light-text-primary dark:text-white">
                {formatFieldValue(sendAt)}
              </Text>
              <CalendarClock
                size={18}
                strokeWidth={2}
                color={colors.text.secondary}
              />
            </Pressable>
          </View>
          <AnchoredPopover
            anchorRef={fieldRef}
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            placement="bottom"
            align="start"
            gap={6}
            trapFocus
          >
            <View className="w-[300px] max-w-full rounded-[12px] p-3 bg-light-surface-primary dark:bg-[#15171C] border border-light-border-secondary dark:border-white/[0.08]">
              <DateTimePicker
                value={sendAt}
                onChange={(next) => setSendAt(next)}
                minDate={new Date()}
                testID="schedule-datetime-picker"
              />
            </View>
          </AnchoredPopover>
        </View>

        {/* Live preview */}
        <View className="rounded-[10px] bg-light-surface-secondary dark:bg-[#1C1F26] px-[14px] py-[12px]">
          <Text className="font-sans text-[15px] text-light-text-primary dark:text-[#E6E8EC]">
            Sends {summary}
          </Text>
        </View>

        {/* Footer */}
        <View className="flex-row items-center justify-end gap-4 pt-1">
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            className="px-2 py-[10px] active:opacity-60"
            testID="schedule-modal-cancel"
          >
            <Text className="font-sans text-[15px] font-semibold text-light-text-primary underline dark:text-white">
              Cancel
            </Text>
          </Pressable>
          <Pressable
            onPress={() => {
              onConfirm(sendAt);
              onClose();
            }}
            accessibilityRole="button"
            className="rounded-pill bg-light-accent-indigo dark:bg-[#537EFF] px-6 py-[12px] active:opacity-90"
            testID="schedule-modal-confirm"
          >
            <Text className="font-sans text-[15px] font-bold text-white">
              Schedule
            </Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}
